// AudioService (ARCHITECTURE 6.1, UI-UX 6.4): один AudioContext, разблокированный кнопкой «Поехали!»;
// фразы заранее декодируются в буферы и играют подряд без новых касаний (на iOS <audio> этого не гарантирует).
export class AudioService {
  private ctx: AudioContext | null = null;
  private buffers = new Map<string, Promise<AudioBuffer | null>>();
  private current: AudioBufferSourceNode | null = null;
  private stopFallback: (() => void) | null = null;
  enabled = true;

  /** Создать или возобновить контекст — только из обработчика касания. */
  async unlock(): Promise<void> {
    if (!this.ctx) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor();
    }
    if (this.ctx.state === "suspended") await this.ctx.resume();
  }

  get unlocked(): boolean {
    return this.ctx?.state === "running";
  }

  private load(url: string): Promise<AudioBuffer | null> {
    let p = this.buffers.get(url);
    if (!p) {
      p = (async () => {
        if (!this.ctx) return null;
        try {
          const r = await fetch(url);
          if (!r.ok) return null;
          return await this.ctx.decodeAudioData(await r.arrayBuffer());
        } catch {
          return null;
        }
      })();
      this.buffers.set(url, p);
    }
    return p;
  }

  /** Заранее декодировать фразы шага. */
  preload(urls: Array<string | undefined>): void {
    for (const u of urls) if (u) void this.load(u);
  }

  stop(): void {
    try {
      this.current?.stop();
    } catch {
      /* уже остановлен */
    }
    this.current = null;
    this.stopFallback?.();
    this.stopFallback = null;
  }

  /**
   * Проиграть фразу; промис завершается по окончании. Если файла озвучки нет (сборка разработчика),
   * используется системный синтез речи — в релизе для пилота каждая фраза озвучена заранее (VAL-07).
   */
  async play(url: string | undefined, text?: string): Promise<void> {
    if (!this.enabled) return;
    this.stop();
    const buf = url ? await this.load(url) : null;
    if (buf && this.ctx) {
      await withTimeout(buf.duration * 1000 + 1000, (resolve) => {
        const src = this.ctx!.createBufferSource();
        src.buffer = buf;
        src.connect(this.ctx!.destination);
        src.onended = () => resolve();
        this.current = src;
        src.start();
      });
      return;
    }
    if (text && "speechSynthesis" in window) {
      // Системный синтез в части браузеров не присылает onend — страховка по длине фразы.
      await withTimeout(Math.min(12_000, 1500 + text.length * 90), (resolve) => {
        const u = new SpeechSynthesisUtterance(text);
        u.lang = "ru-RU";
        u.rate = 0.9;
        u.onend = () => resolve();
        u.onerror = () => resolve();
        this.stopFallback = () => {
          speechSynthesis.cancel();
          resolve();
        };
        speechSynthesis.speak(u);
      });
    }
  }

  /** Короткий позитивный звук (выбор, награда) — без звуков неудачи (PED-4). */
  chime(kind: "tap" | "reward" = "tap"): void {
    if (!this.enabled || !this.ctx) return;
    const notes = kind === "reward" ? [523.25, 659.25, 783.99, 1046.5] : [659.25];
    notes.forEach((f, i) => {
      const o = this.ctx!.createOscillator();
      const g = this.ctx!.createGain();
      o.type = "sine";
      o.frequency.value = f;
      const t = this.ctx!.currentTime + i * 0.09;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);
      o.connect(g).connect(this.ctx!.destination);
      o.start(t);
      o.stop(t + 0.3);
    });
  }
}

/** Промис, который завершается по событию или по таймауту — воспроизведение никогда не блокирует сценарий. */
function withTimeout(ms: number, start: (resolve: () => void) => void): Promise<void> {
  return new Promise<void>((resolve) => {
    let done = false;
    const finish = () => {
      if (!done) {
        done = true;
        resolve();
      }
    };
    const timer = setTimeout(finish, ms);
    start(() => {
      clearTimeout(timer);
      finish();
    });
  });
}

export const audio = new AudioService();
